import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import userApi from "../../api/user/user.api";
import bookingApi from "../../api/booking/BookingApi";
import ProcessingBar2 from "../../components/ProcessingBar/ProcessingBar2";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi";
import {
  HiOutlineUsers,
  HiOutlineCalendar,
  HiOutlineUserRemove,
} from "react-icons/hi";

const PAGE_SIZE = 200;

const getId = (value) => {
  if (!value) return "";

  if (typeof value === "object") {
    return String(value._id || value.id || "");
  }

  return String(value);
};

const CustomersCard = () => {
  const [activeTab, setActiveTab] = useState("all");

  const [customers, setCustomers] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);

  const [page, setPage] = useState(1);
  const [pageCursors, setPageCursors] = useState([null]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [failedRequest, setFailedRequest] = useState(null);

  const [bookedCustomerIds, setBookedCustomerIds] = useState(null);
  const [bookingError, setBookingError] = useState("");

  const requestInProgress = useRef(false);
  const listRef = useRef(null);

  const loadCustomers = useCallback(async (cursor = null, targetPage = 1) => {
    if (requestInProgress.current) return;

    requestInProgress.current = true;
    setIsLoading(true);
    setError("");
    setFailedRequest(null);

    try {
      const { response, err } = await userApi.getAllUsers({
        cursor,
      });

      if (err) throw err;

      const payload = response?.data;

      if (
        !Array.isArray(payload?.users) ||
        !Number.isInteger(payload?.pagination?.total)
      ) {
        throw new Error("Unexpected customers response");
      }

      // Replace the displayed page.
      setCustomers(payload.users);
      setTotalCustomers(payload.pagination.total);
      setNextCursor(payload.pagination.nextCursor);
      setHasMore(Boolean(payload.pagination.hasMore));
      setPage(targetPage);

      // Remember cursors so Previous can reload earlier pages.
      setPageCursors((previous) => {
        const updated = previous.slice(0, targetPage);
        updated[targetPage - 1] = cursor;
        return updated;
      });

      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }
    } catch (err) {
      console.error("Error loading customers:", err);

      setError("Unable to load customers. Please try again.");
      setFailedRequest({ cursor, targetPage });
    } finally {
      requestInProgress.current = false;
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    let active = true;

    const loadBookings = async () => {
      try {
        const { response, err } = await bookingApi.getAllBookings();

        if (err) throw err;

        const bookings = response?.data?.booking;

        if (!Array.isArray(bookings)) {
          throw new Error("Unexpected bookings response");
        }

        const ids = new Set(
          bookings.map((booking) => getId(booking.user)).filter(Boolean),
        );

        if (active) {
          setBookedCustomerIds(ids);
        }
      } catch (err) {
        console.error("Error loading bookings:", err);

        if (active) {
          setBookingError("Unable to load booking information.");
        }
      }
    };

    loadBookings();

    return () => {
      active = false;
    };
  }, []);

  const bookedCustomers = useMemo(
    () =>
      customers.filter((customer) => bookedCustomerIds?.has(getId(customer))),
    [customers, bookedCustomerIds],
  );

  const customersWithNoBookings = useMemo(
    () =>
      bookedCustomerIds === null
        ? []
        : customers.filter(
            (customer) => !bookedCustomerIds.has(getId(customer)),
          ),
    [customers, bookedCustomerIds],
  );

  const currentList =
    activeTab === "all"
      ? customers
      : activeTab === "booked"
        ? bookedCustomers
        : customersWithNoBookings;

  const totalPages = Math.max(1, Math.ceil(totalCustomers / PAGE_SIZE));

  const firstCustomer = customers.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;

  const lastCustomer =
    customers.length > 0 ? (page - 1) * PAGE_SIZE + customers.length : 0;

  const rangeStart = (page - 1) * PAGE_SIZE;
  const rangeEnd = rangeStart + customers.length;

  const tabs = [
    {
      key: "all",
      label: "",
      icon: HiOutlineUsers,
      count: `${rangeStart}–${rangeEnd}`,
    },
    {
      key: "booked",
      label: "Booked",
      icon: HiOutlineCalendar,
      count: bookedCustomers.length,
    },
    {
      key: "no-bookings",
      label: "No",
      icon: HiOutlineUserRemove,
      count: customersWithNoBookings.length,
    },
  ];

  return (
    <div className="bg-drylight dark:bg-neutral-800 rounded-xl border border-border p-2 shadow w-full">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-semibold text-yellowlight">Your Customers</h2>

        <span className="text-sm text-textGray">
          Total:{" "}
          <span className="font-semibold text-text">
            {totalCustomers.toLocaleString()}
          </span>
        </span>
      </div>

      <div className="border-b border-textGray my-4" />

      <div className="flex flex-wrap gap-2 mb-4">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          const waitingForBookings =
            tab.key !== "all" && bookedCustomerIds === null;

          return (
            <button
              key={tab.key}
              type="button"
              aria-pressed={isActive}
              disabled={isLoading || waitingForBookings}
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellowlight disabled:cursor-not-allowed disabled:opacity-40 ${
                isActive
                  ? "border-yellowlight bg-yellowlight text-yellowdark"
                  : "border-border text-textGray hover:border-yellowlight hover:text-yellowdark dark:hover:text-yellowlight"
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />

              <span>{tab.label}</span>

              <span
                className={`rounded-md px-1.5 py-0.5 text-xs tabular-nums ${
                  isActive
                    ? "bg-black/10 text-yellowdark"
                    : "bg-gray-500/10 text-textGray"
                }`}
              >
                {waitingForBookings ? "…" : tab.count}
              </span>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-gray-400 mb-2">
        Showing {firstCustomer.toLocaleString()}–{lastCustomer.toLocaleString()}{" "}
        of {totalCustomers.toLocaleString()} customers. Tab counts cover this
        page only.
      </p>

      <p className="text-xs text-gray-400 mb-4">
        Booking tabs use bookings from one month ago onward.
      </p>

      {bookingError && (
        <p role="alert" className="text-sm text-red-500 mb-3">
          {bookingError}
        </p>
      )}

      <div
        ref={listRef}
        aria-busy={isLoading}
        className="bg-drylight dark:bg-neutral-800 rounded-xl border-t border-border p-0 shadow overflow-y-auto max-h-[400px] scrollbar-thin"
      >
        {isLoading ? (
          <div className="min-h-[200px] flex items-center justify-center">
            <ProcessingBar2 />
          </div>
        ) : currentList.length === 0 ? (
          <p className="text-gray-400 text-sm">
            {error
              ? "Customers could not be loaded."
              : "No customers on this page match this tab."}
          </p>
        ) : (
          currentList.map((member) => {
            const id = getId(member);
            const updatedAt = member.updatedAt
              ? new Date(member.updatedAt)
              : null;

            const dateLabel =
              updatedAt && !Number.isNaN(updatedAt.getTime())
                ? updatedAt.toLocaleDateString("en-NZ", {
                    month: "short",
                    day: "numeric",
                    year: "2-digit",
                  })
                : "";

            return (
              <Link
                key={id}
                to={`/customer/preview/${id}`}
                title={`View ${member.displayName || "customer"}'s profile`}
                className="flex justify-between items-center gap-3 border-b border-border pb-4 mb-4 last:border-none last:pb-0 last:mb-0 hover:bg-hover transition rounded-lg px-0"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={member.photo || "/default-avatar.png"}
                    alt={member.displayName || "Customer"}
                    loading="lazy"
                    className="w-10 h-10 rounded-md object-cover shrink-0"
                  />

                  <div className="text-xs min-w-0 max-w-[200px]">
                    <h3 className="font-medium text-text truncate">
                      {member.displayName || "Unnamed customer"}
                    </h3>

                    <p className="text-gray-400 truncate">{member.phone}</p>

                    <p className="text-gray-400 truncate">{member.username}</p>
                  </div>
                </div>

                <span className="text-xs text-textGray whitespace-nowrap">
                  {dateLabel}
                </span>
              </Link>
            );
          })
        )}
      </div>

      {error && (
        <div role="alert" className="mt-3">
          <p className="text-sm text-red-500">{error}</p>

          <button
            type="button"
            disabled={isLoading || !failedRequest}
            onClick={() =>
              loadCustomers(failedRequest.cursor, failedRequest.targetPage)
            }
            className="mt-2 text-sm text-yellowlight underline disabled:opacity-50"
          >
            Try again
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mt-5 border-t border-border pt-4">
        <button
          type="button"
          aria-label="Previous customer page"
          disabled={isLoading || page === 1}
          onClick={() => loadCustomers(pageCursors[page - 2], page - 1)}
          className="group inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-text transition hover:border-yellowlight hover:bg-yellowlight/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellowlight disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:bg-transparent"
        >
          <HiOutlineChevronLeft className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Previous</span>
        </button>

        <div
          role="status"
          aria-live="polite"
          className="flex items-center justify-center gap-2 text-xs text-textGray sm:text-sm"
        >
          {isLoading ? (
            <>
              <span
                aria-hidden="true"
                className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-yellowlight motion-reduce:animate-none"
              />
              <span>Loading...</span>
            </>
          ) : (
            <>
              <span>Page</span>
              <span className="inline-flex h-8 min-w-[32px] items-center justify-center rounded-lg bg-yellowlight/15 px-2 font-semibold tabular-nums text-yellowdark dark:text-yellowlight">
                {page}
              </span>
              <span>
                of <span className="tabular-nums">{totalPages}</span>
              </span>
            </>
          )}
        </div>

        <button
          type="button"
          aria-label="Next customer page"
          disabled={isLoading || !hasMore}
          onClick={() => loadCustomers(nextCursor, page + 1)}
          className="inline-flex items-center gap-2 rounded-lg border border-yellowlight bg-yellowlight px-3 py-2 text-sm font-semibold text-yellowdark transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellowlight focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:brightness-100"
        >
          <span className="hidden sm:inline">Next</span>
          <HiOutlineChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default CustomersCard;
